import { useEntities } from 'utils/hooks/api';
import {
    Redirect,
    generatePath,
    useLocation,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import { DATA_SOURCES, TIKTOK_COUNTRIES, isCovered } from '@/config/constants';
import { TIKTOK_METADATA_BY_KEY, TIKTOK_SORT_OPTIONS } from 'utils/metrics';
import {
    TikTokDrawerContentIndividualHashtagStarter,
    TikTokDrawerContentStarter,
} from './TikTokDrawerContent';
import { formatGrowth, formatVolume } from 'utils/helpers';
import { useEffect } from 'react';
import {
    useAbortController,
    useCurrentCountry,
    useTikTokData,
    useTikTokWeeks,
} from 'utils/hooks';
import { DataSourceIndustriesOnlyBreadCrumbs } from 'components';
import DataSourceTabsByCoverage from 'components/DataSourceTabsByCoverage';
import Drawer from 'components/Drawer';
import { ExploreDataSourceTableStarter } from 'components/DataVis/DataTable/ExploreDataSourceTable';
import { IndustriesPlaceholderMessage } from 'components/Placeholders';
import NoTikTokDataSection from 'components/NoTikTokDataSection';
import PageBar from 'components/PageBar';
import { PagePadding } from 'components/PagePadding';
import Stack from 'components/Stack';
import { StarterTikTokPresetFilterChips } from 'components/FilterChips/TikTokPresetFilterChips';
import { TikTokNameBodyCol } from 'components/TikTokTable';
import TikTokProFeatureModal from './TikTokProFeatureModal';
import TikTokProPaywall from './TikTokProPaywall';
import dayjs from 'dayjs';
import { getTikTokDownloadOptions } from './utils';
import { mixpanelTrack } from 'utils/app';
import useCountryParam from 'utils/hooks/useCountryParam';
import useDataSourceToggle from 'utils/hooks/useDataSourceToggle';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const metrics = [
    [TIKTOK_METADATA_BY_KEY.non_cum_avg_weekly_views, true],
    [TIKTOK_METADATA_BY_KEY.views, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_WoW, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_WoW, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_MoM_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_MoM_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_QoQ_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_QoQ_rolling, true],
    [TIKTOK_METADATA_BY_KEY.non_cum_increase_YoY_rolling, false],
    [TIKTOK_METADATA_BY_KEY.non_cum_YoY_rolling, false],
    [TIKTOK_METADATA_BY_KEY.total_posts_cnt, true],
    [TIKTOK_METADATA_BY_KEY.partner_paid_partnership_posts_views_prc, true],
    [TIKTOK_METADATA_BY_KEY.eligible_for_commission_posts_views_prc, false],
    [TIKTOK_METADATA_BY_KEY.engagement_prc, false],
];

const sortOptions = Object.values(TIKTOK_SORT_OPTIONS).filter(({ sortBy }) =>
    metrics.some(([{ key }]) => key === sortBy),
);
const filterGroupHashtagColumns = (columns) =>
    columns?.filter(
        (key) => ![TIKTOK_METADATA_BY_KEY.sentiment_score.key].includes(key),
    );

function CategoryTrends({ categoryQuery, categoryId, CategoryComponent }) {
    const { breakdown, type } = useParams();
    const { mutateToWithCountryParam } = useCountryParam();
    const country = useCurrentCountry();
    const trendDimension = type === 'trends' ? 'categories' : type;

    const category = categoryQuery.data?.[0];
    const name = category?.name;

    useEffect(() => {
        if (name && country) {
            const eventName =
                trendDimension === 'brands'
                    ? 'Brand Trends'
                    : 'Category Trends';

            mixpanelTrack(eventName, { name, countryName: country.name });
        }
    }, [name, country, trendDimension]);

    if (
        trendDimension === 'brands' &&
        category?.depth &&
        category.depth !== 'cat1'
    ) {
        return (
            <Redirect
                to={mutateToWithCountryParam(
                    generatePath(
                        '/explore/tiktok/brands/:breakdown(hashtags)?',
                        {
                            breakdown,
                        },
                    ),
                )}
            />
        );
    }

    const hashtags = breakdown === 'hashtags' ? '*' : null;
    const brands = trendDimension === 'brands' ? '*' : null;
    const isSubcategoryTrends = trendDimension === 'categories' && !hashtags;
    const categoryDescendants =
        isSubcategoryTrends && categoryId ? [categoryId] : null;
    const categories = !isSubcategoryTrends && categoryId ? [categoryId] : null;
    const paginateBy = !!hashtags ? 'hashtags' : trendDimension;

    return (
        <CategoryComponent
            category={category}
            categoryId={categoryId}
            categoryQuery={categoryQuery}
            country={country}
            hashtags={hashtags}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            name={name}
            paginateBy={paginateBy}
        />
    );
}

function getTikTokDownloadOptionsStarter(...args) {
    const options = getTikTokDownloadOptions(...args);
    options[1].disabled = true;
    return options;
}

function TikTokCategoryTrends({
    category,
    categoryId,
    country,
    hashtags,
    brands,
    categories,
    categoryDescendants,
    trendDimension,
    paginateBy,
    name,
    mixpanelMeta = { contentKey: 'TikTok' },
    filterSelectMenus = ['tikTokCluster', 'healthStats'],
    defaultInitialSortOption = sortOptions.find(
        ({ sortBy }) => sortBy === metrics[0][0].key,
    ),
}) {
    const controller = useAbortController(categoryId);
    const { week, year, query: weeksQuery } = useTikTokWeeks();
    const dataQuery = useTikTokData({
        fetchAllPages: false,
        per_page: 20,
        order_by: defaultInitialSortOption.sortBy,
        ascending: defaultInitialSortOption.ascending,
        paginateBy,
        enabled: !!(
            week &&
            year &&
            country &&
            (categories || categoryDescendants || brands || hashtags === '*')
        ),
        hashtags,
        brands,
        categories,
        categoryDescendants,
        countries: country ? [country.id] : null,
        week,
        year,
        controller,
    });
    const isLoading = weeksQuery.isLoading || dataQuery.isLoading;

    return (
        <ExploreDataSourceTableStarter
            week={week}
            year={year}
            isLoading={isLoading}
            dataQuery={dataQuery}
            category={category}
            categoryId={categoryId}
            country={country}
            hashtags={hashtags}
            brands={brands}
            categories={categories}
            categoryDescendants={categoryDescendants}
            trendDimension={trendDimension}
            paginateBy={paginateBy}
            name={name}
            tableName="explore-tiktok"
            NameBodyColComponent={TikTokNameBodyCol}
            getDownloadOptions={getTikTokDownloadOptionsStarter}
            source="tiktok"
            mixpanelMeta={mixpanelMeta}
            filterSelectMenus={filterSelectMenus}
            filterColumns={!!hashtags ? filterGroupHashtagColumns : null}
            sortOptions={sortOptions}
            metrics={metrics}
            defaultInitialSortOption={defaultInitialSortOption}
            PresetFilterChipsComponent={StarterTikTokPresetFilterChips}
            DrawerComponent={Drawer}
            DrawerContentDefaultComponent={TikTokDrawerContentStarter}
            DrawerContentHashtagComponent={
                TikTokDrawerContentIndividualHashtagStarter
            }
            ProPaywallComponent={TikTokProPaywall}
            ProFeatureModalComponent={TikTokProFeatureModal}
        />
    );
}

const tikTokMetrics = [
    {
        key: 'non_cum_avg_weekly_views',
        label: 'views',
        formatter: formatVolume,
    },
    { key: 'non_cum_MoM_rolling', label: 'MoM', formatter: formatGrowth },
];

function TikTokBrands({
    breakdown,
    categoryId,
    children,
    generateLocation,
    isNotTikTokMarket,
    location,
    mutateToWithCountryParam,
    geo,
    cat1,
}) {
    const { modes } = useDataSourceToggle();
    return (
        <Stack $space="1.5rem">
            <div
                css={`
                    position: relative;
                    z-index: 1;
                `}
            >
                <DataSourceIndustriesOnlyBreadCrumbs
                    dataSource="TIKTOK"
                    cat1={cat1}
                    geo={geo}
                    generateLocation={generateLocation}
                    {...(categoryId && {
                        id: categoryId,
                        isActive: true,
                    })}
                />
            </div>
            {breakdown === 'hashtags' ? (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    unsupportedTabs={[modes.OVERVIEW, modes.SEARCH]}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/hashtags/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            ) : (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    customOverviewTo={mutateToWithCountryParam(
                        `/explore/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customGoogleTo={mutateToWithCountryParam(
                        `/explore/google/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/brands${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            )}
            {children}
        </Stack>
    );
}

function TikTokCatFives({
    breakdown,
    categoryId,
    children,
    generateLocation,
    isNotTikTokMarket,
    location,
    mutateToWithCountryParam,
    cat1,
    geo,
}) {
    const { modes } = useDataSourceToggle();
    return (
        <Stack $space="1.5rem">
            <div
                css={`
                    position: relative;
                    z-index: 1;
                `}
            >
                <DataSourceIndustriesOnlyBreadCrumbs
                    dataSource="TIKTOK"
                    cat1={cat1}
                    geo={geo}
                    generateLocation={generateLocation}
                    metrics={tikTokMetrics}
                    {...(categoryId && {
                        id: categoryId,
                        isActive: true,
                    })}
                />
            </div>
            {breakdown === 'hashtags' ? (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    unsupportedTabs={[modes.OVERVIEW, modes.SEARCH]}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/hashtags/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            ) : (
                <DataSourceTabsByCoverage
                    entityId={categoryId}
                    routePrefix="/explore"
                    hierarchyHint={{ cat1 }}
                    customMode={true}
                    customOverviewTo={mutateToWithCountryParam(
                        `/explore/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customGoogleTo={mutateToWithCountryParam(
                        `/explore/google/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                    customTikTokTo={location}
                    customInstagramTo={mutateToWithCountryParam(
                        `/explore/instagram/trends${categoryId ? `/${categoryId}` : ''}`,
                    )}
                />
            )}
            {children}
        </Stack>
    );
}

function TikTokTrends() {
    const country = useCurrentCountry();
    const { mutateToWithCountryParam } = useCountryParam();
    const location = useLocation();
    const { path } = useRouteMatch();
    const { breakdown, type, categoryId } = useParams();
    const categoryQuery = useEntities({
        enabled: !!categoryId,
        ids: [categoryId],
        entityType: 'categories',
        dataSource: DATA_SOURCES.TIKTOK,
    });
    const generateLocation = ({ id }) => ({
        pathname: generatePath(path, { breakdown, type, categoryId: id }),
        search: location.search,
    });
    const isNotTikTokMarket =
        country && !TIKTOK_COUNTRIES.includes(country.text_id);
    const DimensionComponent =
        type === 'trends' ? TikTokCatFives : TikTokBrands;
    const industryName = categoryQuery?.data?.[0]?.hierarchies?.[0]?.[1]?.cat1;
    return (
        <PagePadding className="max-w-screen-2xl">
            <PageBar />
            <Stack $space="1.25rem">
                <DimensionComponent
                    breakdown={breakdown}
                    categoryId={categoryId}
                    generateLocation={generateLocation}
                    isNotTikTokMarket={isNotTikTokMarket}
                    location={location}
                    mutateToWithCountryParam={mutateToWithCountryParam}
                    cat1={categoryQuery.data?.[0]?.hierarchies?.[0]?.[1]?.cat1}
                    geo={country?.text_id}
                >
                    {(industryName &&
                        country &&
                        !isCovered({
                            geo: country?.text_id,
                            dataSource: 'TIKTOK',
                            cat1: industryName,
                        })) ||
                    (!categoryId &&
                        country?.text_id &&
                        !TIKTOK_COUNTRIES.includes(country?.text_id)) ? (
                        <NoTikTokDataSection />
                    ) : !categoryId && breakdown !== 'hashtags' ? (
                        <IndustriesPlaceholderMessage />
                    ) : (
                        <CategoryTrends
                            categoryQuery={categoryQuery}
                            categoryId={categoryId}
                            CategoryComponent={TikTokCategoryTrends}
                        />
                    )}
                </DimensionComponent>
            </Stack>
        </PagePadding>
    );
}

export default TikTokTrends;
